import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { MeetingWorkflow } from "./components/MeetingWorkflow";
import { VideoDemo } from "./components/VideoDemo";
import { Features } from "./components/Features";
import { WorkflowBridge } from "./components/WorkflowBridge";
import { HowItWorks } from "./components/HowItWorks";
import { CanvasDemo } from "./components/CanvasDemo";
import { Testimonials } from "./components/Testimonials";
import { Newsletter } from "./components/Newsletter";
import { CTA } from "./components/CTA";
import { Footer } from "./components/Footer";
import { BlogList } from "./blog/BlogList";
import { BlogCategory } from "./blog/BlogCategory";
import { BlogPost } from "./blog/BlogPost";
import { PrivacyPolicy } from "./pages/PrivacyPolicy";
import { TermsOfService } from "./pages/TermsOfService";
import { Contact } from "./pages/Contact";
import { Pricing } from "./pages/Pricing";
import { FAQ } from "./pages/FAQ";
import { Changelog } from "./pages/Changelog";
import { NotFound } from "./pages/NotFound";
import { DocsApi } from "./pages/DocsApi";
import { DocsMcp } from "./pages/DocsMcp";
import { DocsBackups } from "./pages/DocsBackups";
import { DocsStuckVersion } from "./pages/DocsStuckVersion";
import { DocsChromeErrorCode5 } from "./pages/DocsChromeErrorCode5";
import { ToolsIndex } from "./tools/ToolsIndex";
import { ToolPage } from "./tools/ToolPage";
// Demo is an SSR-safe shell that dynamically imports the Excalidraw canvas
// on the client only (see pages/Demo.tsx).
import { Demo } from "./pages/Demo";
import { MeetingNotes } from "./pages/MeetingNotes";
import { Challenge } from "./pages/Challenge";
import { Press } from "./pages/Press";
import { AppleNotesCompanion } from "./pages/AppleNotesCompanion";
import { useSignupAttribution } from "./useSignupAttribution";
import { useMarketingAnalytics } from "./useMarketingAnalytics";

function HomePage() {
  return (
    <>
      <Hero />
      <MeetingWorkflow />
      <VideoDemo />
      <Features />
      <WorkflowBridge />
      <HowItWorks />
      <CanvasDemo />
      <Testimonials />
      <Newsletter />
      <CTA />
    </>
  );
}

export function AppRoutes() {
  // First-touch signup attribution: client-only effect (no-op during SSR).
  useSignupAttribution();
  // Funnel instrumentation: page_view + CTA clicks (client-only).
  useMarketingAnalytics();

  // /demo carries its own app-style header (logo, section label, template
  // chips, the save CTA), so rendering the marketing Navbar above it stacked
  // two full-width white bars and ate a bar's worth of canvas height. Suppress
  // the Navbar on that one route; every other route is untouched.
  // useLocation, not a window read, so the prerender and the hydrating client
  // agree on which routes get a nav.
  const { pathname } = useLocation();
  const isCanvasRoute = pathname === "/demo" || pathname === "/demo/";

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      {!isCanvasRoute && <Navbar />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:category" element={<BlogCategory />} />
        <Route path="/blog/:category/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/meeting-notes" element={<MeetingNotes />} />
        <Route path="/challenge" element={<Challenge />} />
        <Route path="/press" element={<Press />} />
        <Route path="/apple-notes-companion" element={<AppleNotesCompanion />} />
        <Route path="/tools" element={<ToolsIndex />} />
        <Route path="/tools/:slug" element={<ToolPage />} />
        <Route path="/docs/api" element={<DocsApi />} />
        <Route path="/docs/mcp" element={<DocsMcp />} />
        <Route path="/docs/backups" element={<DocsBackups />} />
        <Route path="/docs/troubleshooting-stuck-version" element={<DocsStuckVersion />} />
        <Route path="/docs/troubleshooting-chrome-error-code-5" element={<DocsChromeErrorCode5 />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {/* Same flag as the Navbar above: on /demo the canvas is the whole page,
          with nothing to scroll past. */}
      {!isCanvasRoute && <Footer />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
